// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import EntityGuard from '../../../components/EntityGuard';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useDashboardApi } from '../../../store/api/hooks/dashboardApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import Button from '../../../components/elements/button';
import { downloadExcel } from '../../../_common/functions';
import toast from 'react-hot-toast';

const statusColors: Record<string, { bg: string; color: string }> = {
  'Critical': { bg: '#fee2e2', color: '#dc2626' },
  'Low': { bg: '#ffedd5', color: '#ea580c' },
  'Overstocked': { bg: '#ffedd5', color: '#ea580c' },
  'OK': { bg: '#dcfce7', color: '#16a34a' },
};

function stockStatusCell(params: any) {
  const val = params.value;
  if (!val || val === '--') return <span style={{ color: '#9ca3af' }}>--</span>;
  const s = statusColors[val] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{val}</span>;
}

function qtyCell(params: any) {
  const row = params.data;
  if (!row) return '';
  const val = Number(params.value || 0);
  let color = '#111827';
  if (row.stockStatus === 'Critical') color = '#dc2626';
  else if (row.stockStatus === 'Low') color = '#ea580c';
  return <span style={{ color, fontWeight: 600 }}>{val}</span>;
}

const LowStockReport: React.FC = () => {
  const dashboardApi = useDashboardApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility) params.FacilityIDs = filterParams.facility;
    if (filterParams.department) params.DepartmentIDs = filterParams.department;
    if (filterParams.stockStatus) params.StockStatus = filterParams.stockStatus;

    dashboardApi.getLowStockReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.summary?.totalParts || 0);
        setFilterOptions([
          { key: 'facility', label: 'Facility', options: (d?.facilities || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'stockStatus', label: 'Stock Status', options: [
            { value: 'Critical', label: 'Critical' },
            { value: 'Low', label: 'Low' },
            { value: 'Overstocked', label: 'Overstocked' },
            { value: 'OK', label: 'OK' },
          ]},
        ]);
      },
      (error: any) => { console.error('Failed to fetch low stock:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>) => {
    setFilters(filterMap);
    setCurrentPage(1);
    fetchData(1, filterMap);
  };

  const handleExport = () => {
    const params: any = { PageSize: null, PageNumber: null };
    if (filters.facility) params.FacilityIDs = filters.facility;
    if (filters.department) params.DepartmentIDs = filters.department;
    if (filters.stockStatus) params.StockStatus = filters.stockStatus;
    setIsExporting(true);
    dashboardApi.exportLowStockReportToXLS(
      params,
      (resp: any) => { downloadExcel(resp, 'low_stock_report'); toast.success('Exported successfully!'); setIsExporting(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExporting(false); }
    );
  };

  const columnDefs = [
    { headerName: 'Part Name', field: 'partsName', headerClass: 'bg-[#f0f0f0]', pinned: 'left', minWidth: 220, flex: 1, cellRenderer: titleCell, headerTooltip: 'Part Name' },
    { headerName: 'Part Code', field: 'partsCode', headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Part Code' },
    { headerName: 'Facility', field: 'facilityName', headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Facility' },
    { headerName: 'Unit of Measure', field: 'unitOfMeasure', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: titleCell, headerTooltip: 'Unit of Measure' },
    { headerName: 'Min Qty', field: 'minStock', headerClass: 'bg-[#f0f0f0]', width: 100, type: 'rightAligned', cellRenderer: titleCell, headerTooltip: 'Minimum Stock Level' },
    { headerName: 'Max Qty', field: 'maxStock', headerClass: 'bg-[#f0f0f0]', width: 100, type: 'rightAligned', cellRenderer: titleCell, headerTooltip: 'Maximum Stock Level' },
    { headerName: 'Qty on Hand', field: 'qtyOnHand', headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: qtyCell, headerTooltip: 'Current Quantity on Hand' },
    { headerName: 'Status', field: 'stockStatus', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: stockStatusCell, headerTooltip: 'Stock Status' },
  ];

  return (
    <Layout>
      <EntityGuard entity="Low Stock Report">
      <Header title="Low Stock Report" back="/reports">
        <Header.Right>
          <Button theme='primary' onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} showDateRange={false} onApply={handleApplyFilter} />
        {summary && (
          <div className="bg-white rounded-lg mb-4 flex items-center gap-6 flex-wrap text-[13px]" style={{ padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <span style={{ color: '#111827', fontWeight: 600 }}>Total Parts: {summary.totalParts}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>Critical: {summary.criticalCount}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#ea580c', fontWeight: 600 }}>Low: {summary.lowCount}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#ea580c', fontWeight: 600 }}>Overstocked: {summary.overstockedCount}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#16a34a', fontWeight: 600 }}>OK: {summary.okCount}</span>
          </div>
        )}
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>
      </EntityGuard>
    </Layout>
  );
};

export default LowStockReport;
